import type { ColDef, ColGroupDef, GridOptions } from '@ag-grid-community/core';
import { AueTimeFrame, isFixedTimeYear } from '@cdw-ae/common-data';
import type {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { map, Observable, of, tap } from 'rxjs';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import { DevicesDataService } from '../../../../../common/services/device/devices-data.service';
import { OuPathService } from '../../../../../common/services/ou-path/ou-path.service';
import {
  filterToSupportedStateAnalysisFields,
  StateAnalysisReportFilterSettings,
} from '../../../../../common/services/route-config/configs/filter-bar-config.config';
import { createModelAueByOuReportConfig } from '../configs/model-aue-report.config';
import { ReportData } from '../report-viewer/report-viewer.model';
import { BaseReportHandler } from './base-report.handler';

/**
 * Handler for Model/AUE by OU aggregate reports
 * Manages filtering, grid configuration, and data processing for Model/AUE reports
 */
export class ModelAueReportHandler extends BaseReportHandler {
  private static readonly AUE_COLORS: Record<string, string> = {
    [AueTimeFrame.PastAUE]: TailwindColors.pastelRose,
    [AueTimeFrame.LessThan3Months]: TailwindColors.pastelPeach,
    [AueTimeFrame.LessThan6Months]: TailwindColors.pastelLemon,
    [AueTimeFrame.LessThan12Months]: TailwindColors.pastelLime,
    [AueTimeFrame.GreaterThan12Months]: TailwindColors.pastelPeriwinkle,
    [AueTimeFrame.Unknown]: TailwindColors.gray100,
  };

  private static readonly AUE_SORT_ORDER: Record<string, number> = {
    [AueTimeFrame.PastAUE]: 0,
    [AueTimeFrame.Unknown]: 99999,
    [AueTimeFrame.LessThan3Months]: 1,
    [AueTimeFrame.LessThan6Months]: 2,
    [AueTimeFrame.LessThan12Months]: 3,
    [AueTimeFrame.GreaterThan12Months]: 4,
  };
  /**
   * Static method to load Model/AUE report data from API
   * Fetches device state analysis data, processes it with filters, and returns configured report data
   * @param devicesDataService Service to fetch device data
   * @param filters Optional filters to apply to the data
   * @returns Observable of ReportData ready for display
   */
  static load(
    devicesDataService: DevicesDataService,
    filters?: SelectedFilterField[],
    viewMode: 'relative_time' | 'fixed_time' = 'relative_time',
    rawDataCache?: { data: unknown[] | null }
  ): Observable<ReportData> {
    // Cache hit: skip API call, re-transform cached raw data for new viewMode.
    // The toggle changes only viewMode — both views consume the same API response.
    if (rawDataCache?.data) {
      const processedData = ModelAueReportHandler.transformData(
        rawDataCache.data,
        viewMode
      );
      return of(createModelAueByOuReportConfig(processedData));
    }

    // Get filters to apply (use provided filters or initial filters)
    const allFilters =
      filters || ModelAueReportHandler.getInitialFilters() || [];

    // Filter to only include fields supported by the state-analysis endpoint
    const filtersToApply = filterToSupportedStateAnalysisFields(allFilters);

    // Pass filters to API - filtering happens server-side now
    return devicesDataService.getDeviceStateAnalysis(filtersToApply).pipe(
      tap((response) => {
        if (rawDataCache) rawDataCache.data = response.data;
      }),
      map((deviceStateAnalysisResponse) => {
        // Transform data (no filtering needed - API already filtered)
        const processedData = ModelAueReportHandler.transformData(
          deviceStateAnalysisResponse.data,
          viewMode
        );

        // Create and return report config with processed data
        return createModelAueByOuReportConfig(processedData);
      })
    );
  }
  /**
   * Transform raw data to include AUE formatting, colors, and sort order,
   * then pre-aggregate by the composite key used for AG Grid's tree path
   * (aueFormatted, model, orgUnitPath) and sum deviceCount.
   *
   * The API can return multiple rows for the same (AUE, model, OU) triple
   * when other fields (status, batteryHealth, diskUsed, etc.) differ.
   * Without this pre-aggregation, AG Grid's getDataPath produces duplicate
   * paths and silently overwrites leaf nodes — only one row's deviceCount
   * survives per path, dropping devices from the Grand Total (EDUS-2439).
   *
   * @param data Raw device state analysis data
   * @returns Aggregated rows with AUE display properties; one row per
   *          unique (aueFormatted, model, orgUnitPath) triple
   */
  private static transformData(
    data: unknown[],
    viewMode: 'relative_time' | 'fixed_time' = 'relative_time'
  ): unknown[] {
    const currentYear = new Date().getUTCFullYear();
    const transformed: Record<string, unknown>[] = data.map((item) => {
      const record = item as Record<string, unknown>;
      const timeframe: string =
        viewMode === 'fixed_time'
          ? String(record.aueTimeframeFixed ?? AueTimeFrame.Unknown)
          : String(record.aueTimeframeRelative ?? AueTimeFrame.Unknown);

      return {
        ...record,
        model: record.model || 'Unknown',
        orgUnitPath: record.groupingValue || 'Unknown OU',
        aueFormatted: ModelAueReportHandler.formatAueDate(timeframe),
        aueColor: ModelAueReportHandler.getAueColor(timeframe, currentYear),
        aueSortOrder: ModelAueReportHandler.getAueSortOrder(timeframe),
      };
    });

    // Pre-aggregate by composite key matching getDataPath so AG Grid's
    // aggFunc='sum' sees one row per unique tree path.
    const aggregated = transformed.reduce<
      Record<string, Record<string, unknown>>
    >((acc, row) => {
      const key = `${String(row.aueFormatted)}||${String(row.model)}||${String(row.orgUnitPath)}`;
      const existing = acc[key];
      const deviceCount = Number(row.deviceCount ?? 0);

      if (existing) {
        existing.deviceCount = Number(existing.deviceCount ?? 0) + deviceCount;
      } else {
        // Preserve the first row's display fields. aueColor/aueSortOrder
        // are deterministic given aueTimeframeRelative/aueTimeframeFixed, and
        // aueFormatted (part of the key) is derived from whichever column the
        // active viewMode selects — so all rows in this bucket share the same values.
        acc[key] = { ...row, deviceCount };
      }

      return acc;
    }, {});

    // Inject missing buckets with deviceCount: 0 so all timeframes appear even if empty.
    const existingBuckets = new Set(
      Object.values(aggregated).map((row) => String(row.aueFormatted))
    );

    const expectedBuckets =
      viewMode === 'fixed_time'
        ? ModelAueReportHandler.getExpectedFixedTimeBuckets()
        : ModelAueReportHandler.getExpectedRelativeTimeBuckets();

    expectedBuckets.forEach((timeframeValue) => {
      const formatted = this.formatAueDate(timeframeValue);
      if (!existingBuckets.has(formatted)) {
        aggregated[formatted] = {
          aueFormatted: formatted,
          model: undefined,
          orgUnitPath: undefined,
          aueColor: this.getAueColor(timeframeValue, currentYear),
          aueSortOrder: this.getAueSortOrder(timeframeValue),
          deviceCount: 0,
        };
      }
    });

    // Redistribute per (aueFormatted, model) pair, not across the whole row set.
    // The tree is [aueFormatted, model, orgUnitPath], so the same OU appears under
    // many pairs; redistributeNonLeafData compares every path it is handed, and a
    // flat call would treat /A under one pair as a parent because /A/B exists
    // under a different one.
    const rows = Object.values(aggregated);
    const rowsByBranch = new Map<string, Record<string, unknown>[]>();
    for (const row of rows) {
      const branch = `${String(row.aueFormatted)}||${String(row.model)}`;
      const existing = rowsByBranch.get(branch);
      if (existing) {
        existing.push(row);
      } else {
        rowsByBranch.set(branch, [row]);
      }
    }

    return [...rowsByBranch.values()].flatMap((branchRows) =>
      OuPathService.redistributeNonLeafData(
        branchRows,
        ['deviceCount'],
        'orgUnitPath'
      )
    );
  }

  /**
   * Returns all expected Relative Time bucket values (enum values).
   * Used to ensure all buckets appear in the report even if empty.
   */
  private static getExpectedRelativeTimeBuckets(): string[] {
    return [
      AueTimeFrame.PastAUE,
      AueTimeFrame.LessThan3Months,
      AueTimeFrame.LessThan6Months,
      AueTimeFrame.LessThan12Months,
      AueTimeFrame.GreaterThan12Months,
      AueTimeFrame.Unknown,
    ];
  }

  /**
   * Returns the full set of expected Fixed Time buckets: Past AUE, six calendar-year
   * tiers, and Unknown.
   *
   * Uses `getUTCFullYear()` to match the SQL's `AT TIME ZONE 'UTC'`, ensuring the
   * frontend year aligns with the nightly aggregation regardless of the viewer's timezone.
   * Year labels mirror SQL output: currentYear through currentYear+4 as plain strings,
   * currentYear+5 with a trailing "+" (mirrors `|| '+'` in the Postgres CASE statement).
   */
  private static getExpectedFixedTimeBuckets(): string[] {
    const y = new Date().getUTCFullYear();
    return [
      AueTimeFrame.PastAUE,
      ...Array.from({ length: 5 }, (_, i) => String(y + i)),
      `${y + 5}+`,
      AueTimeFrame.Unknown,
    ];
  }

  private static formatAueDate(timeframe: string): string {
    if (timeframe === AueTimeFrame.PastAUE) {
      return 'Past AUE';
    }
    if (timeframe === AueTimeFrame.Unknown) {
      return 'Unknown';
    }
    if (timeframe === AueTimeFrame.LessThan3Months) {
      return '< 3 months';
    }
    if (timeframe === AueTimeFrame.LessThan6Months) {
      return '< 6 months';
    }
    if (timeframe === AueTimeFrame.LessThan12Months) {
      return '< 12 months';
    }
    if (timeframe === AueTimeFrame.GreaterThan12Months) {
      return '> 12 months';
    }
    return timeframe;
  }

  private static getAueColor(
    timeframe: string,
    currentYear = new Date().getFullYear()
  ): string {
    const enumColor = ModelAueReportHandler.AUE_COLORS[timeframe];
    if (enumColor) {
      return enumColor;
    }

    // endsWith('+') must precede parseInt: "2031+" parses as 2031 and would route to pastelLemon incorrectly.
    if (/^\d{4}\+?$/.test(timeframe)) {
      if (timeframe.endsWith('+')) {
        return TailwindColors.pastelLime;
      }
      const yearInt = parseInt(timeframe, 10);
      return yearInt === currentYear
        ? TailwindColors.pastelPeach
        : TailwindColors.pastelLemon;
    }

    return TailwindColors.gray100;
  }

  private static getAueSortOrder(timeframe: string): number {
    const enumOrder = ModelAueReportHandler.AUE_SORT_ORDER[timeframe];
    if (enumOrder !== undefined) {
      return enumOrder;
    }

    if (isFixedTimeYear(timeframe)) {
      return parseInt(timeframe.replace('+', ''), 10);
    }

    return 999;
  }

  /**
   * No need to pre-calculate total - AG Grid will handle aggregation
   */
  public override initializeWithData(_data: unknown[]): void {
    // No-op - total will be calculated dynamically from the grid
  }

  getColumnDefs(): (ColDef | ColGroupDef)[] {
    const config = this.reportData.aggregateConfig;
    if (!config) return [];

    // Skip the first column (group hierarchy) and return remaining columns
    // The auto-group column will be created by AG Grid
    return config.columns.slice(1).map((col) => {
      if ('field' in col && col.field === 'deviceCount') {
        return {
          ...col,
          aggFunc: 'sum',
          valueFormatter: (params) => {
            // For pinned bottom row (Grand Total), show "Total: X"
            if (params.node?.rowPinned === 'bottom') {
              return `Total: ${params.value ?? 0}`;
            }
            // For top-level AUE date rows (level 0), show "Total: X"
            if (params.node?.level === 0 && params.node?.group) {
              return `Total: ${params.value ?? 0}`;
            }
            // For all other rows, just show the number
            return params.value ?? '';
          },
          cellStyle: (params) => {
            // Style pinned bottom row (Grand Total) like column header
            if (params.node?.rowPinned === 'bottom') {
              const style: Record<string, string> = {
                backgroundColor: TailwindColors.grayHeaderBg,
                color: 'var(--color-primary-gray-400)',
                fontWeight: '700',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
              };
              return style;
            }
            return undefined;
          },
        };
      }
      return col;
    });
  }

  getGridOptions(): GridOptions {
    const config = this.reportData.aggregateConfig;
    if (!config) return {};

    const groupFields = config.groupFields;

    return {
      treeData: true,
      animateRows: true,
      groupDefaultExpanded: 0, // Start collapsed
      getDataPath: (data: Record<string, unknown>) => {
        const path: string[] = [];

        for (const field of groupFields) {
          const raw = data[field];

          // Zero-bucket injected rows have model/orgUnitPath set to undefined.
          // String(undefined) produces "undefined", creating phantom nodes in the
          // AG Grid tree. Break on the first null/undefined to produce a valid
          // shorter path (e.g. ['Past AUE']) instead of a malformed one.
          if (raw === undefined || raw === null) {
            break;
          }

          const value = String(raw);

          if (field === 'orgUnitPath') {
            const ouSegments = OuPathService.parsePathForGrid(value);
            path.push(...ouSegments);
          } else {
            path.push(value);
          }
        }

        return path;
      },
      autoGroupColumnDef: {
        headerName: config.columns[0]?.headerName || 'Group',
        flex: 1,
        cellRendererParams: {
          suppressCount: true,
        },
        valueGetter: (params) => {
          // For pinned bottom row, show "Grand Total" in the group column
          if (params.node?.rowPinned === 'bottom') {
            return 'Grand Total';
          }
          // For tree data nodes, AG Grid uses the key property
          return params.node?.key;
        },
        cellStyle: (params) => {
          // Style pinned bottom row (Grand Total) like column header
          if (params.node?.rowPinned === 'bottom') {
            const style: Record<string, string> = {
              backgroundColor: TailwindColors.grayHeaderBg,
              color: 'var(--color-primary-gray-400)',
              fontWeight: '700',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
            };
            return style;
          }

          // Group nodes get color from first leaf child; zero-bucket leaf nodes read own data.
          if (params.node?.level === 0) {
            const aueColor = params.node.group
              ? params.node.allLeafChildren?.[0]?.data?.aueColor
              : params.data?.aueColor;
            if (aueColor) {
              const style: Record<string, string> = {
                backgroundColor: aueColor,
              };
              return style;
            }
          }
          return undefined;
        },
        comparator: (valueA: string, valueB: string, nodeA, nodeB) => {
          // Level 0 (AUE Date): Sort by aueSortOrder (chronological)
          if (nodeA?.level === 0 && nodeB?.level === 0) {
            const firstChildA = nodeA.allLeafChildren?.[0];
            const firstChildB = nodeB.allLeafChildren?.[0];
            const sortOrderA = firstChildA?.data?.aueSortOrder ?? 999;
            const sortOrderB = firstChildB?.data?.aueSortOrder ?? 999;
            return sortOrderA - sortOrderB;
          }

          // Level 1 (Model): Sort alphabetically
          if (nodeA?.level === 1 && nodeB?.level === 1) {
            const keyA = String(nodeA.key ?? '');
            const keyB = String(nodeB.key ?? '');
            return keyA.localeCompare(keyB);
          }

          // Other levels: default alphabetical sort
          return String(valueA ?? '').localeCompare(String(valueB ?? ''));
        },
      },
      getChildCount: (data: Record<string, unknown>) => {
        return data[config.aggregationField] as number;
      },
      defaultColDef: {
        resizable: true,
        sortable: true,
      },
      onGridReady: (params) => {
        // Apply initial sort to auto-group column (ascending by default)
        const columnApi = params.columnApi;
        if (columnApi) {
          columnApi.applyColumnState({
            state: [{ colId: 'ag-Grid-AutoColumn', sort: 'asc' }],
            defaultState: { sort: null },
          });
        }
      },
      onModelUpdated: (params) => {
        // Calculate grand total from top-level nodes after model is updated
        let grandTotal = 0;
        params.api.forEachNode((node) => {
          // Only sum top-level nodes (level 0 = AUE dates)
          if (node.level === 0 && node.aggData) {
            grandTotal += node.aggData[config.aggregationField] || 0;
          }
        });

        // Set pinned bottom row with the calculated grand total
        // The "Grand Total" label will be handled by the valueGetter in autoGroupColumnDef
        params.api.setPinnedBottomRowData([
          {
            [config.aggregationField]: grandTotal,
          },
        ]);
      },
    };
  }

  static override getFilterBarSettings(): FilterBarField[] {
    return StateAnalysisReportFilterSettings;
  }

  static override getInitialFilters(): SelectedFilterField[] | null {
    return BaseReportHandler.getDefaultActiveStatusFilter(
      StateAnalysisReportFilterSettings
    );
  }

  /**
   * Process raw DeviceStateAnalysisAsset data into Model/AUE format
   * Transforms data to add AUE fields
   */
  static override processRawData(
    rawData: unknown[],
    _filters: SelectedFilterField[],
    viewMode: 'relative_time' | 'fixed_time' = 'relative_time'
  ): unknown[] {
    // Transform the data (adds aueFormatted, aueColor, aueSortOrder)
    return ModelAueReportHandler.transformData(rawData, viewMode);
  }
}
